import React, { createContext, useContext, useState, useCallback } from 'react'
import { Snackbar, Alert, AlertColor } from '@mui/material'

interface NotificationContextType {
  showNotification: (message: string, severity?: AlertColor) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}

interface NotificationProviderProps {
  children: React.ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [severity, setSeverity] = useState<AlertColor>('info')

  const showNotification = useCallback((message: string, severity: AlertColor = 'info') => {
    setMessage(message)
    setSeverity(severity)
    setOpen(true)
  }, [])

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setOpen(false)
  }

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleClose} 
          severity={severity} 
          variant="standard"
          sx={{ 
            width: '100%',
            // Ensure proper contrast for all severity levels
            '&.MuiAlert-standardError': {
              backgroundColor: '#1A0D0F',
              borderColor: '#3B1B1F',
              color: '#F28B82',
              '& .MuiAlert-icon': { color: '#F28B82' }
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: '#0A1F12',
              borderColor: '#1A3D2A',
              color: '#81c784',
              '& .MuiAlert-icon': { color: '#81c784' }
            },
            '&.MuiAlert-standardWarning': {
              backgroundColor: '#1A1409',
              borderColor: '#3B2B17',
              color: '#FFD166',
              '& .MuiAlert-icon': { color: '#FFD166' }
            },
            '&.MuiAlert-standardInfo': {
              backgroundColor: '#0A1420',
              borderColor: '#162A45',
              color: '#64B5F6',
              '& .MuiAlert-icon': { color: '#64B5F6' }
            }
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  )
}
